<template>
    <div>
      <SettingHeaderTab />
  
      <div class="create-page">
  
        <!-- Page header -->
        <div class="page-header">
          <div>
            <h5 class="page-title">Create Customer</h5>
            <p class="page-sub">Add a new customer to your account</p>
          </div>
          <div class="page-actions">
            <button type="button" class="btn-ghost" @click="$router.go(-1)">
              <font-awesome-icon icon="times" /> Cancel
            </button>
            <button class="btn-primary-action" :disabled="crudLoading" @click="createCustomer">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="user-plus" />
              {{ crudLoading ? 'Saving...' : 'Save Customer' }}
            </button>
          </div>
        </div>
  
        <!-- Validation errors -->
        <div v-if="hasErrors" class="error-box">
          <div class="error-box-title">
            <font-awesome-icon icon="exclamation-circle" class="me-2" /> Please fix the following errors:
          </div>
          <ul class="error-list">
            <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
          </ul>
        </div>
  
        <form @submit.prevent="createCustomer" class="form-card">
  
          <!-- ── Personal Info ── -->
          <div class="form-section">
            <div class="section-label">Personal Information</div>
            <div class="fields-grid">
  
              <div class="form-group-styled">
                <label class="form-label-styled">Customer Name <span class="req">*</span></label>
                <input
                  v-model="customer_name"
                  type="text"
                  class="form-input-styled"
                  :class="{ 'input--error': fieldError('customer_name') }"
                  placeholder="Enter full name"
                />
                <p v-if="fieldError('customer_name')" class="field-error">{{ fieldError('customer_name') }}</p>
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input-styled"
                  :class="{ 'input--error': fieldError('email') }"
                  placeholder="Enter email address"
                />
                <p v-if="fieldError('email')" class="field-error">{{ fieldError('email') }}</p>
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">Phone Number</label>
                <input
                  v-model="phone_no"
                  type="tel"
                  class="form-input-styled"
                  :class="{ 'input--error': fieldError('phone_no') }"
                  placeholder="Enter phone number"
                />
                <p v-if="fieldError('phone_no')" class="field-error">{{ fieldError('phone_no') }}</p>
              </div>
  
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- ── Address ── -->
          <div class="form-section">
            <div class="section-label">Address Details</div>
            <div class="fields-grid">
  
              <div class="form-group-styled" style="grid-column: span 3;">
                <label class="form-label-styled">Address</label>
                <input
                  v-model="address"
                  type="text"
                  class="form-input-styled"
                  placeholder="Enter street address"
                />
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">Country</label>
                <select v-model="country_id" class="form-input-styled" @change="getStates">
                  <option value="">Choose country</option>
                  <option v-for="country in countries" :key="country.id" :value="country.id">
                    {{ country.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">State</label>
                <select v-model="state_id" class="form-input-styled" @change="getCities" :disabled="!states.length">
                  <option value="">Choose state</option>
                  <option v-for="state in states" :key="state.id" :value="state.id">
                    {{ state.name }}
                  </option>
                </select>
              </div>
  
              <div class="form-group-styled">
                <label class="form-label-styled">City</label>
                <select v-model="city_id" class="form-input-styled" :disabled="!cities.length">
                  <option value="">Choose city</option>
                  <option v-for="city in cities" :key="city.id" :value="city.id">
                    {{ city.name }}
                  </option>
                </select>
              </div>
  
            </div>
          </div>
  
          <!-- Form footer -->
          <div class="form-footer">
            <button type="button" class="btn-ghost" @click="$router.go(-1)">
              <font-awesome-icon icon="times" /> Cancel
            </button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="user-plus" />
              {{ crudLoading ? 'Saving...' : 'Save Customer' }}
            </button>
          </div>
  
        </form>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  
  export default {
    name: 'CreateCustomer',
    components: { SettingHeaderTab },
  
    setup() {
      const router = useRouter();
      const toast  = useToast();
  
      // ── Form fields ──────────────────────────────────────────────────
      const customer_name = ref('');
      const phone_no      = ref('');
      const email         = ref('');
      const address       = ref('');
      const country_id    = ref('');
      const state_id      = ref('');
      const city_id       = ref('');
  
      // ── Lists ────────────────────────────────────────────────────────
      const countries = ref([]);
      const states    = ref([]);
      const cities    = ref([]);
  
      // ── UI state ─────────────────────────────────────────────────────
      const crudLoading      = ref(false);
      const validationErrors = ref({});
  
      const hasErrors = computed(() => Object.keys(validationErrors.value).length > 0);
  
      const flatErrors = computed(() => Object.values(validationErrors.value).flat());
  
      const fieldError = (field) => validationErrors.value[field]?.[0] ?? null;
  
      // ── Location ─────────────────────────────────────────────────────
      const getStates = async () => {
        if (!country_id.value) return;
        state_id.value = '';
        cities.value   = [];
        city_id.value  = '';
        try {
          const { data } = await axios.get('/states/' + country_id.value);
          states.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      const getCities = async () => {
        if (!state_id.value) return;
        city_id.value = '';
        try {
          const { data } = await axios.get('/cities/' + state_id.value);
          cities.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      const getAllCountries = async () => {
        try {
          const { data } = await axios.get('countries');
          countries.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      // ── Submit ───────────────────────────────────────────────────────
      const createCustomer = async () => {
        crudLoading.value = true;
        validationErrors.value = {};
        try {
          const { data } = await axios.post('settings/customer', {
            customer_name: customer_name.value,
            phone_no:      phone_no.value,
            email:         email.value,
            address:       address.value,
            country_id:    country_id.value,
            state_id:      state_id.value,
            city_id:       city_id.value,
          });
          toast.success(data.message);
          router.push('/all-customers');
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) {
            toast.warning('Please fix the errors below.');
            validationErrors.value = err.response.data.message;
          } else if (status === 403) {
            toast.error(err.response.data.message);
          } else {
            toast.error('An error occurred. Please try again.');
          }
        } finally {
          crudLoading.value = false;
        }
      };
  
      onMounted(getAllCountries);
  
      return {
        customer_name, phone_no, email, address,
        country_id, state_id, city_id,
        countries, states, cities,
        crudLoading, validationErrors, hasErrors, flatErrors, fieldError,
        getStates, getCities, createCustomer,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .create-page { display:flex; flex-direction:column; gap:0; margin-top:20px; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  
  /* ── Error box ───────────────────────────────────────────────────────── */
  .error-box       { background:#fff5f5; border-left:4px solid #e03535; border-radius:0 8px 8px 0; padding:14px 18px; margin-bottom:16px; }
  .error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:8px; display:flex; align-items:center; }
  .error-list      { margin:0; padding-left:18px; }
  .error-list li   { font-size:13px; color:#c02828; margin-bottom:4px; }
  .me-2 { margin-right:8px; }
  
  /* ── Form card ───────────────────────────────────────────────────────── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── Sections ────────────────────────────────────────────────────────── */
  .form-section   { padding:24px 28px; }
  .section-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#7a90b0; margin-bottom:20px; }
  .section-divider { border:none; border-top:1px solid #f0f4f8; }
  
  /* ── Fields grid ─────────────────────────────────────────────────────── */
  .fields-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
  
  /* ── Form elements ───────────────────────────────────────────────────── */
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus   { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .form-input-styled:disabled { background:#f8fafc; color:#7a90b0; cursor:not-allowed; }
  .input--error { border-color:#e03535 !important; }
  .input--error:focus { box-shadow:0 0 0 3px rgba(224,53,53,.08) !important; }
  .field-error { font-size:12px; color:#e03535; margin:2px 0 0; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  
  /* ── Form footer ─────────────────────────────────────────────────────── */
  .form-footer { display:flex; justify-content:flex-end; gap:10px; padding:20px 28px; border-top:1px solid #e4eaf2; background:#f8fafc; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:1024px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:640px)  {
    .fields-grid  { grid-template-columns:1fr; }
    .page-header  { flex-direction:column; }
    .page-actions { width:100%; }
  }
  </style>